import * as THREE from 'three'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { Design } from './design'
import { EditorScene } from './scene'
import { NodoLibero } from './attach'

export type Scelta =
  | { readonly tipo: 'nodo'; readonly nodo: NodoLibero }
  | { readonly tipo: 'parte'; readonly id: string }
  | { readonly tipo: 'fianco'; readonly partId: string; readonly quota: number; readonly azimut: number }

export interface EditorControls {
  attach(dom: HTMLElement): void
  update(camera: THREE.PerspectiveCamera, altezzaRazzo: number, centroY: number): void
  /** cosa c'e sotto il puntatore: prima i nodi, che stanno sopra a tutto */
  punta(ev: PointerEvent, dom: HTMLElement, camera: THREE.PerspectiveCamera,
        scena: EditorScene, design: Design, catalog: PartCatalog): Scelta | null
  /** true se l'ultimo gesto e stato un trascinamento, non un clic */
  haTrascinato(): boolean
}

export function createEditorControls(): EditorControls {
  let yaw = 0.9
  let pitch = 0.18
  let distanzaVoluta = 0
  let distanza = 0
  let inquadrato = false

  let giu = false
  let mosso = 0
  let ultimoX = 0
  let ultimoY = 0

  const raycaster = new THREE.Raycaster()

  return {
    attach(dom) {
      dom.addEventListener('pointerdown', (e) => {
        giu = true; mosso = 0; ultimoX = e.clientX; ultimoY = e.clientY
      })
      window.addEventListener('pointerup', () => { giu = false })
      window.addEventListener('pointermove', (e) => {
        if (!giu) return
        const dx = e.clientX - ultimoX
        const dy = e.clientY - ultimoY
        mosso += Math.abs(dx) + Math.abs(dy)
        yaw -= dx * 0.007
        pitch = Math.max(-1.4, Math.min(1.4, pitch + dy * 0.007))
        ultimoX = e.clientX; ultimoY = e.clientY
      })
      dom.addEventListener('wheel', (e) => {
        e.preventDefault()
        distanzaVoluta = Math.max(4, Math.min(400, distanzaVoluta * Math.exp(e.deltaY * 0.0013)))
      }, { passive: false })
    },

    update(camera, altezzaRazzo, centroY) {
      if (!inquadrato || distanzaVoluta === 0) {
        distanzaVoluta = Math.max(14, altezzaRazzo * 1.9)
        distanza = distanzaVoluta
        inquadrato = true
      }
      distanza += (distanzaVoluta - distanza) * 0.15

      const bersaglio = new THREE.Vector3(0, centroY, 0)
      camera.up.set(0, 1, 0)
      camera.position.set(
        bersaglio.x + Math.cos(yaw) * Math.cos(pitch) * distanza,
        bersaglio.y + Math.sin(pitch) * distanza,
        bersaglio.z + Math.sin(yaw) * Math.cos(pitch) * distanza,
      )
      camera.lookAt(bersaglio)
    },

    haTrascinato: () => mosso > 6,

    punta(ev, dom, camera, scena, design, catalog) {
      const r = dom.getBoundingClientRect()
      const p = new THREE.Vector2(
        ((ev.clientX - r.left) / r.width) * 2 - 1,
        -((ev.clientY - r.top) / r.height) * 2 + 1,
      )
      raycaster.setFromCamera(p, camera)

      for (const hit of raycaster.intersectObjects(scena.nodiPuntabili, false)) {
        const n = scena.nodoDi(hit.object)
        if (n) return { tipo: 'nodo', nodo: n }
      }

      const colpi = raycaster.intersectObjects(scena.partiPuntabili, false)
      if (colpi.length === 0) return null
      const hit = colpi[0]!
      const id = scena.parteDi(hit.object)
      if (!id) return null

      // il punto colpito, letto in coordinate del veicolo, dice a che quota e
      // a che azimut agganciare una parte radiale
      const parte = design.vessel.parts.find((x) => x.id === id)!
      const def = partDef(catalog, parte.defId)
      const azimut = Math.atan2(hit.point.z - parte.position.z, hit.point.x - parte.position.x)
      const quota = hit.point.y - parte.position.y
      const fiancheggiabile = def.shape.kind !== 'box'

      return fiancheggiabile
        ? { tipo: 'fianco', partId: id, quota, azimut }
        : { tipo: 'parte', id }
    },
  }
}
